import MainLayout from "@/components/layout/admin/MainLayout.vue";
import AdvancedLaravelVuePaginate from 'advanced-laravel-vue-paginate';
import 'advanced-laravel-vue-paginate/dist/advanced-laravel-vue-paginate.css';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import axios from 'axios';
import CONFIG from "@/assets/js/config.js";
import Select2Multiple from 'v-select2-multiple-component';
import SelectAllService from '@/services/SelectAllService.js';

export default {
  name: "PaymentAttributeDetailList",
  components: {
    MainLayout,
    AdvancedLaravelVuePaginate,
    Loading,
    Select2Multiple,
  },
  mixins: [SelectAllService],

  data() {
    return {
      isFilterOpen: false,
      isLoading: false,
      paymentData: [],
      paginationData: {},
      positionOptions: [],
      currency: '',
      search_status: false,
      rowErrors: {},
      searchData: {
        type: "",
        empNumber: "",
        positionId: "",
        empName: "",
      },
      searchErrors: {},
      CONFIG,
      noDataMsg: 'No payment attribute details found.',
    };
  },

  watch: {
    currentCbs: {
      handler() {
        this.rowErrors = {};
      },
      deep: true,
    },
    selectedIds() {
      this.rowErrors = {};
    },
    selectedAllToggle() {
      this.rowErrors = {};
    },
    selectedAllCb() {
      this.rowErrors = {};
    },
  },

  methods: {
    getSearchError(field) {
      if (!this.searchErrors || !this.searchErrors[field]) return '';
      const msg = Array.isArray(this.searchErrors[field]) ? this.searchErrors[field][0] : this.searchErrors[field];
      return msg || '';
    },

    async getList(page = 1) {
      this.search_status = false;
      this.isLoading = true;
      try {
        this.rowErrors = {};
        const res = await axios.get('/paymentAttributeDetail/list?page=' + page);
        if (res.data) {
          this.paginationData = res.data;
          const list = this.paginationData.data || [];
          this.currentCbs = [];
          this.paymentData = list.map(p => {
            const key = String(p.employee_id);
            this.storeCurretCbs(key);
            return p;
          });
          this.currency = this.paginationData.currency || '';
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.$store?.commit?.('logout');
          window.location.replace('login');
          return;
        }
        console.error('Error fetching payment attribute details', error);
        this.$notification?.error?.(error.response?.data?.message || 'Failed to load payment attribute details.', {
          timer: 5,
          position: 'bottomCenter',
        });
      } finally {
        this.isLoading = false;
      }
    },

    async getPosition() {
      try {
        const response = await axios.get('/position/all');
        this.positionOptions = response.data.map(pos => ({
          id: pos.id,
          text: pos.position_name
        }));
      } catch (error) {
        console.error("Failed to fetch positions", error);
      }
    },

    reset() {
      this.rowErrors = {};
      this.searchData = {
        type: "",
        empNumber: "",
        positionId: "",
        empName: "",
      };
      this.searchErrors = {};
      this.isSearched = false;
      this.search_status = false;
      this.isSearch = 0;
      this.resetSelectedAll();
      this.currentCbs = [];
      this.isLoading = true;
      this.getList(1);
    },

    async search(page = 1) {
      this.search_status = true;
      this.searchErrors = {};
      this.isLoading = true;
      try {
        this.rowErrors = {};
        this.resetSelectedAll();
        this.searchedData = { ...this.searchData };
        this.isSearched = true;
        const response = await axios.post('/paymentAttributeDetail/search', {
          ...this.searchData, page: page
        });
        this.paginationData = response.data;
        const list = this.paginationData.data || [];
        this.currentCbs = [];
        this.paymentData = list.map(p => {
          const key = String(p.employee_id);
          this.storeCurretCbs(key);
          return p;
        });
      } catch (error) {
        if (error.response?.status === 401) {
          this.$store?.commit?.('logout');
          window.location.replace('login');
          return;
        }
        if (error.response?.status === 422) {
          this.searchErrors = error.response?.data?.errors || {};
        }
        console.error('Search failed', error);
        this.$notification?.error?.(error.response?.data?.message || 'Search failed. Please try again.', {
          timer: 5,
          position: 'bottomCenter',
        });
      } finally {
        this.isLoading = false;
      }
    },

    handlePaginate(page) {
      this.rowErrors = {};
      this.getList(page);
    },

    /** Returns true if user has at least one row selected (explicit or via select-all). */
    hasSelection() {
      const hasExplicit = Array.isArray(this.selectedIds) && this.selectedIds.length > 0;
      return this.selectedAllToggle || hasExplicit;
    },

    /** Build search payload for calculate API. */
    getCalculateSearchPayload() {
      return {
        type: this.searchData.type,
        empNumber: this.searchData.empNumber,
        positionId: this.searchData.positionId,
        empName: this.searchData.empName,
      };
    },

    /** Fetch one page of list (search or default) and return data array. */
    async fetchListPage(page) {
      if (this.search_status) {
        const res = await axios.post('/paymentAttributeDetail/search', {
          ...this.searchData,
          page,
        });
        const data = res.data;
        return (data && (data.data || data)) ? (data.data || data) : [];
      }
      const res = await axios.get('/paymentAttributeDetail/list?page=' + page);
      const data = res.data;
      return (data && (data.data || data)) ? (data.data || data) : [];
    },

    /** Resolve owner keys to send: either all pages (minus exceptions) or explicit selectedIds. */
    async resolveOwnerKeysForCalculate() {
      const total = this.paginationData?.total ?? 0;
      const perPage = this.paginationData?.per_page ?? this.paginationData?.perPage ?? (this.paymentData?.length || 10);
      const lastPage = this.paginationData?.last_page ?? Math.ceil(total / perPage);

      if (!this.selectedAllToggle) {
        return Array.isArray(this.selectedIds) ? [...this.selectedIds] : [];
      }

      const allKeySet = new Set();
      for (let page = 1; page <= lastPage; page++) {
        const list = await this.fetchListPage(page);
        list.forEach(p => {
          allKeySet.add(String(p.employee_id));
        });
      }

      if (this.selectedIds?.length) {
        const exclude = new Set(this.selectedIds.map(String));
        return Array.from(allKeySet).filter(k => !exclude.has(String(k)));
      }
      return Array.from(allKeySet);
    },

    /** Clear selection and refresh current view. */
    async clearSelectionAndRefresh() {
      this.resetSelectedAll();
      this.currentCbs = [];
      const page = this.paginationData?.current_page ?? 1;
      if (this.search_status) {
        await this.search(page);
      } else {
        await this.getList(page);
      }
    },

    showCalculateError(message) {
      this.$notification?.error?.(message, { timer: 3, position: 'bottomCenter' });
    },

    showCalculateSuccess(message) {
      this.$notification?.success?.(message, { timer: 3, position: 'bottomCenter' });
    },

    buildIndexedEmployeeErrorMap(errors, employeeIds) {
      if (!errors || typeof errors !== 'object') return '';
      const map = {};
      Object.entries(errors).forEach(([field, msgs]) => {
        const match = String(field).match(/^employee_ids\.(\d+)$/);
        if (!match) return;
        const index = Number(match[1]);
        const employeeId = employeeIds?.[index];
        const message = Array.isArray(msgs) ? msgs.join(' ') : String(msgs ?? '');

        if (!employeeId) return;
        if (message) map[String(employeeId)] = message;
      });
      return map;
    },

    async calculate() {
      this.rowErrors = {};
      if (!this.hasSelection()) {
        this.showCalculateError('Select at least one payment detail');
        return;
      }
      if (!window.confirm('Are you sure you want to calculate for selected employees?')) {
        return;
      }

      this.isLoading = true;
      let employee_ids = [];
      try {
        const ownerKeys = await this.resolveOwnerKeysForCalculate();
        if (!ownerKeys.length) {
          this.showCalculateError('Select at least one payment detail');
          return;
        }

        employee_ids = ownerKeys.map(k => Number(k)).filter(v => Number.isFinite(v) && v > 0);
        const res = await axios.post('/paymentAttributeDetail/calculate', {
          employee_ids,
          search: this.getCalculateSearchPayload(),
        });

        const data = res?.data;
        if (data?.success && data.processed !== undefined) {
          await this.clearSelectionAndRefresh();
          const msg = data.processed > 0
            ? `Payment calculation completed for ${data.processed} employee(s).`
            : 'No employees could be calculated (missing data or already processed).';
          this.showCalculateSuccess(msg);
        } else {
          this.showCalculateError(data?.message || 'Batch calculation did not complete successfully.');
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.$store?.commit?.('logout');
          window.location.replace('login');
          return;
        }
        const status = error.response?.status;
        const errors = error.response?.data?.errors || {};
        if (status === 422 && errors) {
          const map = this.buildIndexedEmployeeErrorMap(errors, employee_ids);
          this.rowErrors = map;
          if (Object.keys(map).length > 0) {
            return;
          }
          const fallback = Object.values(errors).flat().join(' ');
          this.showCalculateError(fallback || 'Validation failed.');
          return;
        }
        const message = error.response?.data?.message || 'Batch calculation failed. Please try again.';
        this.showCalculateError(message);
        console.error('Payment calculate API Error', error);
      } finally {
        this.isLoading = false;
      }
    },

    async generatePayslip() {
      this.rowErrors = {};
      if (!this.hasSelection()) {
        this.showCalculateError('Select at least one payment detail to generate payslip.');
        return;
      }
      if (!window.confirm('Generate payslip(s) for selected employees?')) {
        return;
      }

      this.isLoading = true;
      let requestedEmployeeIds = [];
      try {
        const ownerKeys = await this.resolveOwnerKeysForCalculate();
        if (!ownerKeys.length) {
          this.showCalculateError('Select at least one payment detail to generate payslip.');
          return;
        }

        const employee_ids = ownerKeys.map(k => Number(k)).filter(v => Number.isFinite(v) && v > 0);
        requestedEmployeeIds = employee_ids;
        const res = await axios.post('/payslip/generate', { employee_ids });

        const data = res?.data;
        const isError = res?.status >= 400 || data?.error;
        if (isError) {
          this.showCalculateError(data?.message || data?.error || 'Payslip generation did not complete successfully.');
        } else {
          await this.clearSelectionAndRefresh();
          this.showCalculateSuccess(data?.message || `Payslip(s) generated for ${employee_ids.length} employee(s).`);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          this.$store?.commit?.('logout');
          window.location.replace('login');
          return;
        }
        const status = error.response?.status;
        const errors = error.response?.data?.errors || {};

        if (status === 422 && errors) {
          const map = this.buildIndexedEmployeeErrorMap(errors, requestedEmployeeIds);
          this.rowErrors = map;
          if (!Object.keys(map).length) {
            const fallback = Object.values(errors).flat().join(' ');
            this.showCalculateError(fallback || 'Validation failed.');
          }
          return;
        }

        this.showCalculateError('Payslip generation failed. Please try again.');
        console.error('Payslip generate API Error', error);
      } finally {
        this.isLoading = false;
      }
    },

    selectToggle(id, index) {
      this.selectToggleWithCount(id, index, this.paymentData.length);
    },
  },

  mounted() {
    this.isLoading = true;
    this.isSearch = 0;
    this.getList();
    this.getPosition();
  }
};
